'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { useCompany } from '@/features/settings/hooks/use-settings';
import { caenApi, type CaenMigration } from '@/shared/services/caen-api';

/**
 * Banner Dashboard pentru reminder migrare CAEN Rev. 2 → Rev. 3.
 *
 * Vizibil când:
 * - Tenant are caenCode setat
 * - Codul există în Rev. 2 (legacy) și NU în Rev. 3
 * - User n-a dismiss-uit în sesiunea curentă
 *
 * Termen oficial: 25 septembrie 2026 (HG 284/2025).
 */

const CAEN_DEADLINE = new Date('2026-09-25T23:59:59');
const DISMISS_KEY = 'caen-migration-banner-dismissed';

export function CaenMigrationBanner() {
  const { data: company } = useCompany();
  const [migration, setMigration] = useState<CaenMigration | null>(null);
  const [checking, setChecking] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Hydrate dismissed state from localStorage on mount
  useEffect(() => {
    try {
      const v = localStorage.getItem(DISMISS_KEY);
      if (v === '1') setDismissed(true);
    } catch {
      // ignore
    }
  }, []);

  const caenCode = (company as any)?.caenCode as string | undefined;

  // Resolve CAEN code status (Rev. 3 already → no banner; Rev. 2 → show migration targets)
  useEffect(() => {
    if (!caenCode || caenCode.trim().length < 4) {
      setMigration(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    (async () => {
      try {
        const rev3 = await caenApi.byCode(caenCode, 'rev3').catch(() => null);
        if (cancelled) return;
        if (rev3) {
          setMigration(null);
          return;
        }
        const m = await caenApi.migrate(caenCode).catch(() => null);
        if (cancelled) return;
        setMigration(m);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caenCode]);

  const daysLeft = useMemo(() => {
    return Math.ceil((CAEN_DEADLINE.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }, []);

  if (dismissed || checking || !migration) return null;

  const isPastDeadline = daysLeft < 0;
  const isUrgent = daysLeft >= 0 && daysLeft <= 30;
  const tone = isPastDeadline ? 'red' : isUrgent ? 'red' : 'amber';

  const targetPreview = migration.targets.length === 1
    ? `${migration.targets[0]!.code}${migration.targets[0]!.label ? ' — ' + (migration.targets[0]!.label).slice(0, 80) : ''}`
    : `${migration.targets.length} variante (vezi în Setări)`;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className={
        'rounded-xl border p-4 flex items-start gap-3 ' +
        (tone === 'red'
          ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30'
          : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30')
      }
    >
      <AlertTriangle
        className={
          'h-5 w-5 mt-0.5 flex-shrink-0 ' + (tone === 'red' ? 'text-red-500' : 'text-amber-500')
        }
      />
      <div className="flex-1 min-w-0">
        <p
          className={
            'text-sm font-semibold ' +
            (tone === 'red' ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300')
          }
        >
          {isPastDeadline
            ? `Cod CAEN Rev. 2 (${caenCode}) — termen migrare depășit`
            : `Cod CAEN Rev. 2 (${caenCode}) — ${daysLeft} ${daysLeft === 1 ? 'zi rămasă' : 'zile rămase'} pentru migrare`}
        </p>
        <p
          className={
            'text-sm mt-0.5 ' +
            (tone === 'red' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400')
          }
        >
          Codul tău CAEN este din vechea clasificare Rev. 2. Conform HG 284/2025, toate firmele
          trebuie să se actualizeze la <b>CAEN Rev. 3 până la 25 septembrie 2026</b>.
          {migration.targets.length > 0 && (
            <> Recomandare: <span className="font-medium">{targetPreview}</span>.</>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          href="/setari?tab=company"
          className={
            'inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ' +
            (tone === 'red'
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-amber-600 text-white hover:bg-amber-700')
          }
        >
          Migrează acum
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className={
            'text-xs px-2 py-1 rounded hover:bg-white/40 dark:hover:bg-black/20 transition-colors ' +
            (tone === 'red' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400')
          }
          aria-label="Amână"
        >
          Mai târziu
        </button>
      </div>
    </div>
  );
}
